import { useMemo, useCallback } from 'react';
import { pick } from 'js/utils/ramda.util';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as config from '../../../config';
import * as actions from '../../../store/actions';
import { useOnPeriodChange } from './useOnPeriodChange';
import { ANOTHER_PERIOD_VALUE, LAST_SIX_MONTHS, FILTERS } from '../../../constants';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../../store';

export const useFilters = () => {
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const onPeriodChange = useOnPeriodChange();
    const {
        statusFilterEnabled, transactionTypeFilterEnabled,
        anotherPeriodEnabled, sixMonthsPeriodEnabled
    } = useComponentConfig();
    const {
        isSummaryLoading, isHistoryLoading, isExportLoading
    } = useDynamicReducerSelector(pick([
        'isSummaryLoading', 'isHistoryLoading', 'isExportLoading'
    ]));
    const isLoading = isSummaryLoading || isHistoryLoading || isExportLoading;
    const periodOptions = useMemo(() => config.periods.filter(option =>
        (option.value !== ANOTHER_PERIOD_VALUE || anotherPeriodEnabled)
        && (option.value !== LAST_SIX_MONTHS || sixMonthsPeriodEnabled)),
    [anotherPeriodEnabled, sixMonthsPeriodEnabled]);
    const parseOptions = useCallback((options) => options.map(option => ({
        ...option, label: translate(option.label)
    })), [translate]);

    const onFilterChange = useCallback((event) => {
        const { name, value } = event.target;

        dispatch(actions.changeFilter({ [name]: value }));
    }, [dispatch]);

    return useMemo(() => {
        const tableFilters = [{
            id: 0,
            name: FILTERS.PERIOD,
            disabled: isLoading,
            onChange: onPeriodChange,
            options: parseOptions(periodOptions),
            label: translate('filter.title.period'),
            'aria-label': translate(['filter.title.period', 'playtech.mobile.system.select.aria-label'])
        }];

        if (statusFilterEnabled) {
            tableFilters.push({
                id: 1,
                name: FILTERS.STATUS,
                disabled: isLoading,
                onChange: onFilterChange,
                options: parseOptions(config.statuses),
                label: translate('filter.title.status'),
                'aria-label': translate(['filter.title.status', 'playtech.mobile.system.select.aria-label'])
            });
        }

        if (transactionTypeFilterEnabled) {
            tableFilters.push({
                id: 2,
                name: FILTERS.TYPE,
                disabled: isLoading,
                onChange: onFilterChange,
                options: parseOptions(config.types),
                label: translate('filter.title.type'),
                'aria-label': translate(['filter.title.type', 'playtech.mobile.system.select.aria-label'])
            });
        }

        return tableFilters;
    }, [
        isLoading, periodOptions, onFilterChange, statusFilterEnabled,
        transactionTypeFilterEnabled, translate, parseOptions, onPeriodChange
    ]);
};
